import { computed } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { SUPPORTED_LOCALES, type SupportedLocale } from '@/i18n';
import { useUiDisplayLocale } from '@/composables/useUiDisplayLocale';
import { resolvePageFromPathname } from '@/navTree';
import { useContentStore } from '@/stores/content';

function localeNameKey(locale: SupportedLocale): `nav.localeName.${SupportedLocale}` {
  return `nav.localeName.${locale}`;
}

function isSupportedLocaleStr(value: string): value is SupportedLocale {
  return (SUPPORTED_LOCALES as readonly string[]).includes(value);
}

/**
 * Wagtail locale of the page currently shown (listing row), which can differ from the URL
 * prefix (e.g. `/en/...` synthetic URL while only a Tetum translation exists).
 */
export function usePathVsUiLocaleMismatch() {
  const route = useRoute();
  const { t } = useI18n();
  const contentStore = useContentStore();
  const uiLocale = useUiDisplayLocale();

  const contentPageLocale = computed((): SupportedLocale | null => {
    const items = contentStore.expandedListingItems;
    if (!items.length) return null;
    const byId =
      contentStore.currentPageId != null
        ? items.find((i) => i.id === contentStore.currentPageId)
        : null;
    const page = byId ?? resolvePageFromPathname(items, route.path);
    const raw = page?.meta.locale;
    if (!raw || !isSupportedLocaleStr(raw)) return null;
    return raw;
  });

  const isMismatch = computed(
    () => contentPageLocale.value != null && contentPageLocale.value !== uiLocale.value
  );

  const contentPageLabel = computed(() =>
    contentPageLocale.value != null ? t(localeNameKey(contentPageLocale.value)) : ''
  );

  const uiLocaleLabel = computed(() => t(localeNameKey(uiLocale.value)));

  /** Short phrase for inline hint (drawer / panel). */
  const hintShort = computed(() =>
    isMismatch.value
      ? t('nav.contentLocaleMismatchHint', { locale: contentPageLabel.value })
      : ''
  );

  /** Longer explanation for `title` and screen readers. */
  const hintTitle = computed(() =>
    isMismatch.value
      ? t('nav.contentLocaleMismatchTitle', {
          content: contentPageLabel.value,
          ui: uiLocaleLabel.value,
        })
      : ''
  );

  return {
    contentPageLocale,
    uiLocale,
    isMismatch,
    contentPageLabel,
    hintShort,
    hintTitle,
  };
}
